'use client';

import React from 'react';
import { useFilterSetUtils } from 'src/hooks/filters/useFilterSetUtils';
import { EmptyState } from 'src/components';
import { ManageFilter } from './components';
import { ParentFilterType, ParentFilterGroup } from 'src/context/reducers/filterSetReducer';

export interface FilterSet {
  id: string;
  label: string;
  type: ParentFilterType;
  scope: 'personal' | 'company';
}

export interface ManageFiltersProps {
  section: string;
  className?: string;
  'data-testid'?: string;
}

const ManageFilters: React.FC<ManageFiltersProps> = ({
  section,
  className = '',
  'data-testid': testId = 'manage-filters-container',
}) => {
  // Get all filters without filtering by company status
  const { filterSets, error } = useFilterSetUtils({
    searchQuery: '',
  });

  if (error) {
    return (
      <EmptyState
        size="sm"
        title="Error Loading Filters"
        description="There was an error loading the filters. Please try again later."
        data-testid={`${testId}-error`}
      />
    );
  }

  // Filter by scope to match the section name (case-insensitive) and exclude CODE type
  const sectionFilters = filterSets.filter(
    (filterSet) =>
      filterSet.scope.toLowerCase() === section.toLowerCase() &&
      filterSet.type !== 'CODE'
  );

  return (
    <div
      className={`p-4 ${className} flex flex-col gap-2`}
      data-testid={testId}
      role="region"
      aria-label={`${section} filters`}
    >
      <div
        className="flex flex-col flex-wrap gap-2 p-3 bg-white"
        data-testid={`${testId}-list-all-filters`}
        role="list"
        aria-label={`${section} filters`}
      >
        {sectionFilters.length ? (
          sectionFilters.map((filterSet) => (
            <ManageFilter
              key={filterSet.id}
              filterSet={filterSet as ParentFilterGroup}
              data-testid={`${testId}-item-${filterSet.id}`}
            />
          ))
        ) : (
          <EmptyState
            size="sm"
            title={`No ${section.toLowerCase()} filters available`}
            description="Create a filter set to get started."
            data-testid={`${testId}-empty-all-filters`}
          />
        )}
      </div>
    </div>
  );
};

export default ManageFilters;
